import { getBackendBaseUrl } from '../utils/apiUrls';
import { firstIncompleteStepIndex as firstIncompleteWizardStep } from './childFormWizardFields';

/**
 * API client for email-initiated form creation sessions (zero-login magic link).
 */

const BACKEND_BASE = getBackendBaseUrl();

export const FORMS_API_URL = `${BACKEND_BASE}/forms`;

const FORM_SESSION_ERROR_MESSAGES = {
  consumed: 'This form has already been completed.',
  expired: 'This link has expired. Please email tc@keyflo.ai to request a new form.',
  not_found: 'This link is invalid. Please email tc@keyflo.ai to request a new form.',
  cancelled: 'This form session was cancelled.',
};

/**
 * Prefer the server human-readable message over machine error codes.
 */
export function getFormSessionErrorMessage(err, fallback = 'Unable to load this form link.') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;

  const apiMessage = err.apiMessage || err.message;
  const code = err.error || err.code;

  if (apiMessage && String(apiMessage).trim() && apiMessage !== code) {
    return String(apiMessage).trim();
  }
  if (code && FORM_SESSION_ERROR_MESSAGES[code]) {
    return FORM_SESSION_ERROR_MESSAGES[code];
  }
  if (code) return String(code);
  return fallback;
}

class FormSessionError extends Error {
  constructor({ status, error, apiMessage, message }) {
    super(message || apiMessage || error || 'Form session error');
    this.name = 'FormSessionError';
    this.status = status;
    this.error = error;
    this.apiMessage = apiMessage;
  }
}

function throwFormSessionError(res, data) {
  const code = data?.error;
  const apiMessage = data?.message || (code && FORM_SESSION_ERROR_MESSAGES[code]) || code;
  throw new FormSessionError({
    status: res.status,
    error: code,
    apiMessage,
    message: apiMessage,
  });
}

async function parseFormSessionResponse(res) {
  try {
    return await res.json();
  } catch {
    if (!res.ok) {
      throw new FormSessionError({
        status: res.status,
        error: 'submit_failed',
        apiMessage: 'Submit failed. Please try again.',
        message: 'Submit failed. Please try again.',
      });
    }
    return {};
  }
}

export async function fetchFormSession(token) {
  const res = await fetch(`${FORMS_API_URL}/sessions/${token}`);
  const data = await parseFormSessionResponse(res);
  if (!res.ok) throwFormSessionError(res, data);
  return data;
}

export async function startFormSession(token) {
  const res = await fetch(`${FORMS_API_URL}/sessions/${token}/start`, { method: 'POST' });
  const data = await parseFormSessionResponse(res);
  if (!res.ok) throwFormSessionError(res, data);
  return data;
}

export async function fetchFormSessionProfile(token) {
  const res = await fetch(`${FORMS_API_URL}/sessions/${token}/profile`);
  const data = await parseFormSessionResponse(res);
  if (!res.ok) throwFormSessionError(res, data);
  return data;
}

const SUBMIT_POLL_MS = 1500;
const SUBMIT_POLL_ATTEMPTS = 80;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function sessionAsSubmitResult(session) {
  if (session?.status !== 'submitted' || !session?.created_entity_id) return null;
  const splitId = session.split_attachment_id || session.splitAttachmentId;
  return {
    success: true,
    entity_type: session.created_entity_type,
    entity_id: session.created_entity_id,
    already_submitted: true,
    ...(splitId ? { split_attachment_id: splitId } : {}),
  };
}

export async function waitForFormSessionResult(
  token,
  { attempts = SUBMIT_POLL_ATTEMPTS, delayMs = SUBMIT_POLL_MS } = {},
) {
  for (let i = 0; i < attempts; i += 1) {
    const session = await fetchFormSession(token);
    const result = sessionAsSubmitResult(session);
    if (result) return result;
    if (session.status !== 'submitting') break;
    await sleep(delayMs);
  }
  throw new FormSessionError({
    status: 409,
    error: 'in_progress',
    apiMessage: 'Your listing is still being created. Please wait a moment.',
    message: 'Your listing is still being created. Please wait a moment.',
  });
}

export async function submitFormSession(token, payload) {
  const res = await fetch(`${FORMS_API_URL}/sessions/${token}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await parseFormSessionResponse(res);
  if (res.ok) return data;
  if (data?.error === 'consumed' && data?.entity_id) {
    return {
      success: true,
      entity_type: data.entity_type,
      entity_id: data.entity_id,
      already_submitted: true,
      ...(data.split_attachment_id ? { split_attachment_id: data.split_attachment_id } : {}),
    };
  }
  if (data?.error === 'in_progress' || data?.retryable) {
    return waitForFormSessionResult(token);
  }
  throwFormSessionError(res, data);
}

/**
 * Find first wizard step index where required visible fields are still empty.
 */
export function firstIncompleteStepIndex(steps, values, optionalFields = []) {
  return firstIncompleteWizardStep(steps, values, optionalFields);
}
